'use client';
/**
 * DÉTACHER UNE PERSONNE DE SON COMPTE — le geste inverse de `RattacherCompte`,
 * RQ seul (03/10/2026, décision RQ).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Quatre messages du moteur disaient « détachez-le d'abord » sans qu'aucune
 * route ne le fasse : le lien ne se défaisait qu'en base. Voici la porte.
 *
 * ⚠️ DÉTACHER N'EST PAS FERMER. Le compte reste ouvert et connectable ; on dit
 * seulement que cette personne n'en est plus la titulaire. Suspendre ou
 * archiver est un AUTRE geste (Paramétrage → Utilisateurs) — les confondre
 * ferait d'une correction de rattachement une révocation d'accès, ou l'inverse.
 * La fenêtre le dit, parce que c'est la méprise la plus naturelle.
 *
 * ⚠️ L'IDENTIFIANT NE REVIENT JAMAIS À QUELQU'UN D'AUTRE. Il part à
 * l'historique de la fiche ; sans cela il paraîtrait libre, et un acte ancien
 * se lirait un jour sous le nom de l'arrivant.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { nomDe } from '@/lib/nom';
import { inputStyle, Modale as Fenetre } from '@/components/ui';
import type { RhPersonnel } from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
/** Même seuil que le moteur : de quoi écrire une raison, pas seulement « ok ». */
const MOTIF_MIN = 20;

export function DetacherCompte({ personne, identifiant, onFait }: {
  personne: RhPersonnel;
  /** L'identifiant de connexion dont on détache la personne — nommé dans la
   *  fenêtre : « détacher le compte » sans dire lequel se confirme à l'aveugle. */
  identifiant: string;
  onFait: () => void;
}) {
  const { showToast } = useGed();
  const [ouvert, setOuvert] = React.useState(false);
  const [motif, setMotif] = React.useState('');
  const [envoi, setEnvoi] = React.useState(false);

  const detacher = async () => {
    setEnvoi(true);
    try {
      const r = await fetchEcriture(`/api/ged/ged/rh/personnel/${encodeURIComponent(personne.orgId)}/detacher-compte`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ motif: motif.trim() }),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) { showToast(d?.error?.message || 'Détachement refusé.', 'erreur'); return; }
      setOuvert(false); setMotif('');
      onFait();
    } finally { setEnvoi(false); }
  };

  if (!ouvert) {
    return (
      <Bouton taille="sm" onClick={() => setOuvert(true)} title="Cette personne n’est plus titulaire de ce compte. L’accès, lui, ne se ferme pas ici.">
        Détacher le compte
      </Bouton>
    );
  }
  const assez = motif.trim().length >= MOTIF_MIN;
  return (
    <Fenetre titre="Détacher le compte" largeur={620} onClose={() => setOuvert(false)}
      pied={<>
        <Bouton taille="sm" onClick={() => setOuvert(false)}>Annuler</Bouton>
        <Bouton variante="primaire" disabled={!assez || envoi} onClick={() => void detacher()}>
          {envoi ? 'Détachement…' : 'Détacher ce compte'}
        </Bouton>
      </>}>
      <div style={{ display: 'grid', gap: 12 }}>
        <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.55 }}>
          <strong>{nomDe(personne)}</strong> ne sera plus titulaire du compte{' '}
          <span style={{ fontFamily: 'var(--font-mono)' }}>{identifiant}</span>.
        </p>
        {/* ⚠️ LES DEUX CONSÉQUENCES, DITES AVANT LE GESTE : l'une rassure (rien
            n'est fermé), l'autre engage (l'identifiant est consommé). */}
        <ul style={{ fontSize: 12.5, margin: 0, paddingLeft: 18, lineHeight: 1.6, color: 'var(--text-muted)' }}>
          <li>
            Le compte <strong>n’est ni fermé ni suspendu</strong> : il reste connectable. Couper l’accès
            se décide au <strong>Paramétrage → Utilisateurs</strong>.
          </li>
          <li>
            Ce qui a été fait sous ce compte <strong>reste attribué</strong> à cette personne, et
            l’identifiant <span style={{ fontFamily: 'var(--font-mono)' }}>{identifiant}</span> ne sera
            <strong> jamais réattribué</strong> à quelqu’un d’autre.
          </li>
          <li>Le compte repart <strong>sans libellé</strong> : il s’affichera sous son identifiant.</li>
        </ul>
        <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>
          Pourquoi ce détachement ?
          <textarea value={motif} onChange={(e) => setMotif(e.currentTarget.value)} rows={3} maxLength={300}
            placeholder="Erreur de rattachement, départ de l’établissement, compte repris par quelqu’un d’autre…"
            style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
          <span style={{ fontSize: 11.5, color: assez ? 'var(--text-muted)' : 'var(--amber-600)' }}>
            {assez
              ? 'Cette phrase part au journal : c’est elle qu’on relira si les actes signés ensuite ne collent pas.'
              : `Encore ${MOTIF_MIN - motif.trim().length} caractère(s) — la raison part au journal.`}
          </span>
        </label>
      </div>
    </Fenetre>
  );
}
